import { Link } from "@inertiajs/react";
import { Check, DatabaseZap, ShieldCheck } from "lucide-react";
import type { ForgeDestination } from "@/frontend/features/Fde/data/types";

interface Props {
  destinations: ForgeDestination[];
  effectiveDestinationId: string | null;
  copy: Record<string, string>;
  isAdmin: boolean;
}

export default function DestinationPanel({
  destinations,
  effectiveDestinationId,
  copy,
  isAdmin,
}: Props) {
  return (
    <div className="h-full overflow-y-auto p-4 md:p-6">
      <div className="max-w-3xl">
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/20 p-4">
          <ShieldCheck className="mt-0.5 size-5 text-primary" />
          <div>
            <h3 className="text-sm font-semibold">{copy.destination_policy}</h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {copy.destination_policy_description}
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {destinations.map((destination) => {
            const selected = destination.id === effectiveDestinationId;
            return (
              <article
                key={destination.id}
                className={`rounded-lg border p-4 ${selected ? "border-primary/50 bg-primary/5" : "border-border"}`}
              >
                <div className="flex items-start gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background">
                    <DatabaseZap className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="truncate text-sm font-semibold">
                        {destination.name}
                      </h4>
                      {selected && <Check className="size-4 text-primary" />}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {destination.kind} · {destination.environment}
                      {destination.region ? ` · ${destination.region}` : ""}
                    </p>
                  </div>
                </div>
                {destination.description && (
                  <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                    {destination.description}
                  </p>
                )}
              </article>
            );
          })}
        </div>
        {destinations.length === 0 && (
          <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {copy.no_destination}
          </p>
        )}
        {isAdmin && (
          <Link
            href="/cc/forge_destinations"
            className="mt-5 inline-flex text-sm font-medium text-primary hover:underline"
          >
            {copy.manage_destinations}
          </Link>
        )}
      </div>
    </div>
  );
}
